import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomDatePickerComponent } from '../../../core/shared/components/custom-date-picker/custom-date-picker.component';
import { AuthService } from '../../../core/services/auth.service';
import { UsersService } from '../../../core/services/users.service';
import { AppointmentsService } from '../../../core/services/appointments.service';
import { LoaderService } from '../../../core/shared/services/loader.service';
import { environment } from '../../../../environments/environment';
import { TimeFormatPipe } from '../../../core/shared/pipes/time-format.pipe';
import { USER_TYPES } from '../../../core/shared/constants';
import Quill from 'quill';

interface Session {
  id: string;
  menteeName: string;
  menteeAvatar?: string;
  date: Date;
  time: string;
  duration: number;
  milestone: string;
  status: 'pending' | 'completed' | 'cancelled';
  sessionUrl?: string;
  notes?: string;
  files?: AppointmentFile[];
}

interface CalendarDay {
  day: number;
  date: Date;
  isOtherMonth: boolean;
  isSelected: boolean;
  isAvailable: boolean;
}

interface TimeSlot {
  time: string;
  selected: boolean;
}

interface AppointmentFile {
  name: string;
  size: number;
  type: string;
  url: string;
  uploadDate: Date;
}

@Component({
  selector: 'app-agenda',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomDatePickerComponent, TimeFormatPipe],
  templateUrl: './agenda.component.html',
  styleUrls: ['./agenda.component.scss']
})
export class AgendaComponent implements OnInit {
  // Servicios
  private authService = inject(AuthService);
  private usersService = inject(UsersService);
  private appointmentsService = inject(AppointmentsService);
  private loaderService = inject(LoaderService);

  // Usuario actual
  user: any = null;

  // Modal de confirmación
  showConfirmationModal: boolean = false;

  // Modal de alerta
  showAlertModal: boolean = false;
  alertTitle: string = '';
  alertMessage: string = '';
  alertType: 'info' | 'warning' | 'error' | 'success' = 'info';

  // Filtros
  searchTerm: string = '';
  statusFilter: string = '';
  stageFilter: string = '';
  dateFromFilter: Date | string | null = null;
  dateToFilter: Date | string | null = null;

  // Datos de sesiones
  sessions: Session[] = [];
  filteredSessions: Session[] = [];
  paginatedSessions: Session[] = [];
  private assignedCustomersMap: Map<string, any> = new Map();

  // Paginación
  currentPage: number = 1;
  pageSize: number = 5;
  totalPages: number = 1;

  // Ordenamiento
  sortField: string = 'date';
  sortDirection: 'asc' | 'desc' = 'asc';

  // Utilidad para template
  Math = Math;

  // Calendar properties
  currentDate: Date = new Date();
  currentYear: number = this.currentDate.getFullYear();
  currentMonth: number = this.currentDate.getMonth();
  selectedDate: Date | null = null;
  calendarDays: CalendarDay[] = [];
  weekDays: string[] = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];

  // Notes modal
  showNotesModal: boolean = false;
  selectedAppointment: Session | null = null;
  appointmentNotes: string = '';
  appointmentFiles: AppointmentFile[] = [];
  isSavingNotes: boolean = false;
  showPreview: boolean = false;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('quillEditor', { static: false }) quillEditor?: ElementRef;
  quillInstance: Quill | null = null;

  // Time slots
  timeSlots: TimeSlot[] = [
    { time: '07:00 AM', selected: false },
    { time: '08:00 AM', selected: false },
    { time: '09:00 AM', selected: false },
    { time: '10:00 AM', selected: false },
    { time: '11:00 AM', selected: false },
    { time: '12:00 PM', selected: false },
    { time: '02:00 PM', selected: false },
    { time: '03:00 PM', selected: false },
    { time: '04:00 PM', selected: false },
    { time: '05:00 PM', selected: false },
    { time: '06:00 PM', selected: false },
    { time: '07:00 PM', selected: false },
    { time: '08:00 PM', selected: false },
    { time: '09:00 PM', selected: false }
  ];

  constructor(private router: Router) {}

  ngOnInit(): void {
    // Cargar usuario actual
    this.user = this.authService.getUser();
    console.log('Usuario actual:', this.user);
    
    this.loadSessions();
    // Seleccionar la fecha actual por defecto
    this.selectedDate = new Date();
    this.generateCalendar();
    
    // Cargar disponibilidad del mentor
    this.loadMentorAvailability();
  }

  isExpertUser(): boolean {
    if (!this.user) return false;

    // Verificar por el tipo de usuario
    if (this.user.type && this.user.type.id === USER_TYPES.Expert.id) {
      return true;
    }

    // O verificar si tiene el rol en el array de roles
    if (
      this.user.roles &&
      Array.isArray(this.user.roles) &&
      this.user.roles.length > 0
    ) {
      return this.user.roles.some(
        (role: any) => typeof role === "object" && role && role.name === "Experto"
      );
    }

    return false;
  }

  private loadSessions(): void {
    if (!this.user || !this.user._id) {
      console.warn('No hay usuario autenticado para cargar sesiones');
      this.sessions = [];
      this.applyFilters();
      return;
    }

    this.loaderService.show();
    
    this.usersService.findCustomersByCoach(this.user._id, '').subscribe({
      next: (response) => {
        this.populateAssignedCustomers(response?.data);
        this.fetchCoachAppointments();
      },
      error: (error) => {
        console.error('Error al cargar clientes asignados:', error);
        this.assignedCustomersMap.clear();
        this.fetchCoachAppointments();
      }
    });
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = [...this.sessions];

    // Filtro por búsqueda
    if (this.searchTerm) {
      filtered = filtered.filter(session => 
        session.menteeName.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }

    // Filtro por estado
    if (this.statusFilter) {
      filtered = filtered.filter(session => session.status === this.statusFilter);
    }

    // Filtro por etapa
    if (this.stageFilter) {
      filtered = filtered.filter(session => 
        session.milestone.toLowerCase().includes(this.stageFilter.toLowerCase())
      );
    }

    // Filtro por fecha desde
    if (this.dateFromFilter) {
      console.log('🔍 Valor original dateFromFilter:', this.dateFromFilter);
      // Manejar correctamente el formato YYYY-MM-DD para evitar problemas de zona horaria
      let fromDate: Date;
      if (typeof this.dateFromFilter === 'string' && this.dateFromFilter.includes('-')) {
        const [year, month, day] = this.dateFromFilter.split('-');
        fromDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        fromDate = new Date(this.dateFromFilter);
      }
      fromDate.setHours(0, 0, 0, 0); // Inicio del día
      console.log('🔍 Filtro desde:', fromDate);
      filtered = filtered.filter(session => {
        const sessionDate = new Date(session.date);
        sessionDate.setHours(0, 0, 0, 0); // Inicio del día
        const isIncluded = sessionDate >= fromDate;
        console.log(`📅 Sesión ${session.menteeName} - Fecha: ${sessionDate.toDateString()}, Incluida: ${isIncluded}`);
        return isIncluded;
      });
    }

    // Filtro por fecha hasta
    if (this.dateToFilter) {
      console.log('🔍 Valor original dateToFilter:', this.dateToFilter);
      // Manejar correctamente el formato YYYY-MM-DD para evitar problemas de zona horaria
      let toDate: Date;
      if (typeof this.dateToFilter === 'string' && this.dateToFilter.includes('-')) {
        const [year, month, day] = this.dateToFilter.split('-');
        toDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        toDate = new Date(this.dateToFilter);
      }
      toDate.setHours(23, 59, 59, 999); // Final del día
      console.log('🔍 Filtro hasta:', toDate);
      filtered = filtered.filter(session => {
        const sessionDate = new Date(session.date);
        sessionDate.setHours(0, 0, 0, 0); // Inicio del día
        const isIncluded = sessionDate <= toDate;
        console.log(`📅 Sesión ${session.menteeName} - Fecha: ${sessionDate.toDateString()}, Incluida: ${isIncluded}`);
        return isIncluded;
      });
    }

    this.filteredSessions = filtered;
    this.currentPage = 1;
    this.sortField = 'date';
    this.sortDirection = 'desc';
    this.sortBy(this.sortField);
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }

    this.filteredSessions.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (field) {
        case 'menteeName':
          aValue = a.menteeName;
          bValue = b.menteeName;
          break;
        case 'date':
          aValue = this.getSessionTimestamp(a);
          bValue = this.getSessionTimestamp(b);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return this.sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    this.updatePagination();
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredSessions.length / this.pageSize);
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedSessions = this.filteredSessions.slice(startIndex, endIndex);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    
    if (this.totalPages <= maxVisible) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, start + maxVisible - 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < this.totalPages) {
        pages.push(-1); // -1 representa "..."
        pages.push(this.totalPages);
      }
    }
    
    return pages;
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  enterSession(session: Session): void {
    console.log('Ingresar a sesión:', session);
    
    if (session.status === 'pending') {
      // Para sesiones pendientes, generar enlace de Meet y abrir en nueva pestaña
      const meetingLink = this.generateMeetingLink(session);
      if (meetingLink) {
        window.open(meetingLink, '_blank');
      } else {
        this.showAlert('Sesión no disponible', 'La sesión no está disponible aún.', 'warning');
      }
    } else if (session.status === 'completed') {
      // Para sesiones completadas, ver la grabación
      if (session.sessionUrl && session.sessionUrl !== '#') {
        window.open(session.sessionUrl, '_blank');
      } else {
        this.showAlert('Grabación no disponible', 'La grabación no está disponible aún.', 'warning');
      }
    } else {
      this.showAlert('Sesión cancelada', 'Esta sesión ha sido cancelada.', 'error');
    }
  }

  deleteSession(session: Session): void {
    console.log('Eliminar sesión:', session);
    
    if (confirm('¿Estás seguro de que quieres eliminar esta sesión?')) {
      this.loaderService.show();
      
      this.appointmentsService.delete(session.id).subscribe({
        next: (response) => {
          console.log('Sesión eliminada exitosamente:', response);
          this.loaderService.hide();
          this.showAlert('Sesión eliminada', 'La sesión ha sido eliminada exitosamente.', 'success');
          // Recargar las sesiones
          this.loadSessions();
        },
        error: (error) => {
          console.error('Error al eliminar sesión:', error);
          this.loaderService.hide();
          this.showAlert('Error', 'No se pudo eliminar la sesión. Inténtalo de nuevo.', 'error');
        }
      });
    }
  }

  // Generar enlace de Meet usando la lógica del dashboard-executive
  private generateMeetingLink(session: Session): string {
    if (!session.id) {
      console.error('ID de sesión no disponible');
      return '';
    }
    
    // Usar el ID de la sesión como meetingId, igual que en dashboard-executive
    const meetingId = session.id;
    const meetingLink = `${environment.meetingUrl}${meetingId}`;
    
    console.log('🔗 Enlace de Meet generado:', meetingLink);
    return meetingLink;
  }

  // Calendar methods
  generateCalendar(): void {
    this.calendarDays = [];
    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
    const startDate = new Date(firstDay);
    
    // Ajustar para que empiece en lunes
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    // Generar 42 días (6 semanas)
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      const isOtherMonth = date.getMonth() !== this.currentMonth;
      const isSelected = !!(this.selectedDate && 
        date.getDate() === this.selectedDate.getDate() && 
        date.getMonth() === this.selectedDate.getMonth() && 
        date.getFullYear() === this.selectedDate.getFullYear());
      
      // No marcar como available para evitar conflictos visuales
      const isAvailable = false;

      this.calendarDays.push({
        day: date.getDate(),
        date: date,
        isOtherMonth: isOtherMonth,
        isSelected: isSelected,
        isAvailable: isAvailable
      });
    }
  }

  previousMonth(): void {
    this.currentMonth--;
    if (this.currentMonth < 0) {
      this.currentMonth = 11;
      this.currentYear--;
    }
    this.generateCalendar();
  }

  nextMonth(): void {
    this.currentMonth++;
    if (this.currentMonth > 11) {
      this.currentMonth = 0;
      this.currentYear++;
    }
    this.generateCalendar();
  }

  getCurrentMonthName(): string {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[this.currentMonth];
  }

  selectDate(day: CalendarDay): void {
    if (!day.isOtherMonth) {
      this.selectedDate = day.date;
      this.generateCalendar(); // Regenerar para actualizar la selección
      // Actualizar horarios disponibles para la nueva fecha
      this.updateTimeSlotsForSelectedDate();
    }
  }

  toggleTimeSlot(timeSlot: TimeSlot): void {
    timeSlot.selected = !timeSlot.selected;
  }

  confirmAvailability(): void {
    const selectedTimeSlots = this.timeSlots.filter(slot => slot.selected);
    
    // Validar que al menos un horario esté seleccionado
    if (selectedTimeSlots.length === 0) {
      this.showAlert('Selección requerida', 'Debe seleccionar al menos un horario antes de guardar.', 'warning');
      return;
    }

    // Validar que hay una fecha seleccionada
    if (!this.selectedDate) {
      this.showAlert('Fecha requerida', 'Debe seleccionar una fecha antes de guardar.', 'warning');
      return;
    }

    // Validar que hay un usuario autenticado
    if (!this.user || !this.user._id) {
      this.showAlert('Usuario no encontrado', 'No se encontró información del usuario. Por favor, inicie sesión nuevamente.', 'error');
      return;
    }

    // Mostrar modal de confirmación
    this.showConfirmationModal = true;
  }

  // Método para obtener horarios seleccionados (para el modal)
  getSelectedTimeSlots(): TimeSlot[] {
    return this.timeSlots.filter(slot => slot.selected);
  }

  // Cerrar modal de confirmación
  closeConfirmationModal(): void {
    this.showConfirmationModal = false;
  }

  // Mostrar modal de alerta personalizado
  showAlert(title: string, message: string, type: 'info' | 'warning' | 'error' | 'success' = 'info'): void {
    this.alertTitle = title;
    this.alertMessage = message;
    this.alertType = type;
    this.showAlertModal = true;
  }

  // Cerrar modal de alerta
  closeAlertModal(): void {
    this.showAlertModal = false;
    this.alertTitle = '';
    this.alertMessage = '';
  }

  // Proceder con la confirmación después del modal
  proceedWithConfirmation(): void {
    const selectedTimeSlots = this.timeSlots.filter(slot => slot.selected);
    
    console.log('Disponibilidad confirmada:', {
      date: this.selectedDate,
      timeSlots: selectedTimeSlots
    });

    // Formatear la fecha seleccionada
    const formattedDate = this.formatDateToString(this.selectedDate!);
    
    // Preparar los datos para enviar al backend
    const availabilityData = {
      day: formattedDate,
      times: selectedTimeSlots.map(slot => slot.time)
    };

    console.log('Datos de disponibilidad a enviar:', availabilityData);
    console.log('Horarios seleccionados con formato AM/PM:', selectedTimeSlots.map(slot => slot.time));

    // Obtener la disponibilidad actual del usuario
    this.loaderService.show();
    this.usersService.findById(this.user._id).subscribe({
      next: (response) => {
        if (response && response.data) {
          const userData = response.data;
          let currentAvailability = userData.coachAvailability || [];
          
          // Verificar si ya existe disponibilidad para esta fecha
          const existingIndex = currentAvailability.findIndex((item: any) => item.day === formattedDate);
          
          if (existingIndex >= 0) {
            // Actualizar disponibilidad existente
            currentAvailability[existingIndex] = availabilityData;
          } else {
            // Agregar nueva disponibilidad
            currentAvailability.push(availabilityData);
          }

          // Preparar datos para actualizar
          const updateData = {
            _id: this.user._id,
            coachAvailability: currentAvailability
          };

          // Actualizar disponibilidad en el backend
          this.usersService.updateCoachAvailability(updateData).subscribe({
            next: (updateResponse) => {
              console.log('Disponibilidad actualizada exitosamente:', updateResponse);
              this.loaderService.hide();
              this.showConfirmationModal = false;
              // alert(`Disponibilidad confirmada para ${this.selectedDate?.toLocaleDateString()} con ${selectedTimeSlots.length} horarios seleccionados.`);
            },
            error: (error) => {
              console.error('Error al actualizar disponibilidad:', error);
              this.loaderService.hide();
              // alert('Error al guardar la disponibilidad. Por favor, intente nuevamente.');
            }
          });
        }
      },
      error: (error) => {
        console.error('Error al cargar datos del usuario:', error);
        this.loaderService.hide();
        this.showAlert('Error de carga', 'Error al cargar datos del usuario. Por favor, intente nuevamente.', 'error');
      }
    });
  }

  private formatDateToString(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  // Cargar disponibilidad del mentor desde el backend
  private loadMentorAvailability(): void {
    if (!this.user || !this.user._id) {
      console.log('No hay usuario autenticado para cargar disponibilidad');
      return;
    }

    this.loaderService.show();
    this.usersService.findById(this.user._id).subscribe({
      next: (response) => {
        if (response && response.data) {
          const userData = response.data;
          console.log('Datos del usuario cargados:', userData);
          console.log('CoachAvailability:', userData.coachAvailability);
          
          // Actualizar horarios para la fecha seleccionada
          this.updateTimeSlotsForSelectedDate(userData.coachAvailability);
        }
        this.loaderService.hide();
      },
      error: (error) => {
        console.error('Error al cargar disponibilidad del mentor:', error);
        this.loaderService.hide();
      }
    });
  }

  // Actualizar horarios disponibles basado en la fecha seleccionada
  private updateTimeSlotsForSelectedDate(availabilityData?: any[]): void {
    if (!this.selectedDate) {
      console.log('No hay fecha seleccionada');
      return;
    }

    const formattedDate = this.formatDateToString(this.selectedDate);
    console.log('Actualizando horarios para fecha:', formattedDate);

    // Si no se proporcionan datos, usar los datos del usuario actual
    let coachAvailability = availabilityData;
    if (!coachAvailability && this.user && this.user.coachAvailability) {
      coachAvailability = this.user.coachAvailability;
    }

    if (!coachAvailability || !Array.isArray(coachAvailability)) {
      console.log('No hay datos de disponibilidad disponibles');
      // Resetear todos los horarios a no seleccionados
      this.timeSlots.forEach(slot => slot.selected = false);
      return;
    }

    // Buscar disponibilidad para la fecha seleccionada
    const dayAvailability = coachAvailability.find((item: any) => item.day === formattedDate);
    
    if (dayAvailability && dayAvailability.times && Array.isArray(dayAvailability.times)) {
      console.log('Horarios disponibles encontrados para', formattedDate, ':', dayAvailability.times);
      
      // Obtener horarios ocupados para esta fecha
      const occupiedTimes = this.getOccupiedTimesForDate(formattedDate);
      console.log('Horarios ocupados para', formattedDate, ':', occupiedTimes);
      
      // Actualizar el estado de los horarios
      this.timeSlots.forEach(slot => {
        // Normalizar los horarios para comparación consistente
        const normalizedSlotTime = this.normalizeTimeFormat(slot.time);
        const normalizedAvailableTimes = dayAvailability.times.map((time: string) => this.normalizeTimeFormat(time));
        const isAvailable = normalizedAvailableTimes.includes(normalizedSlotTime);
        
        // Convertir horarios ocupados (24h) a formato 12h para comparar con slots
        const occupiedTimes12h = occupiedTimes.map(time24h => {
          const converted = this.convertTo12Hour(time24h);
          const normalized = this.normalizeTimeFormat(converted);
          console.log(`Convirtiendo ${time24h} -> ${converted} -> ${normalized}`);
          return normalized;
        });
        const isOccupied = occupiedTimes12h.includes(normalizedSlotTime);
        
        // Solo marcar como seleccionado si está en la disponibilidad Y no está ocupado
        slot.selected = isAvailable && !isOccupied;
        
        // Debug logging para cada horario
        console.log(`Horario ${slot.time} (normalizado: ${normalizedSlotTime}):`, {
          isAvailable,
          isOccupied,
          finalSelected: slot.selected,
          availableTimes: dayAvailability.times,
          normalizedAvailableTimes: normalizedAvailableTimes,
          occupiedTimes24h: occupiedTimes,
          occupiedTimes12h: occupiedTimes12h,
          matchFound: occupiedTimes12h.includes(normalizedSlotTime)
        });
      });
    } else {
      console.log('No hay horarios disponibles para la fecha:', formattedDate);
      // Resetear todos los horarios a no seleccionados
      this.timeSlots.forEach(slot => slot.selected = false);
    }
  }

  // Obtener horarios ocupados para una fecha específica
  private getOccupiedTimesForDate(date: string): string[] {
    if (!this.user || !this.user.coachAppointments || !Array.isArray(this.user.coachAppointments)) {
      return [];
    }

    const occupiedTimes: string[] = [];
    
    this.user.coachAppointments.forEach((appointment: any) => {
      if (appointment.date === date && appointment.time) {
        console.log('Procesando cita:', appointment);
        
        // Los horarios ocupados ya están en formato 24h, mantenerlos así
        let timeFormatted = appointment.time;
        console.log('Tiempo ocupado (formato 24h):', timeFormatted);
        
        occupiedTimes.push(timeFormatted);
      }
    });

    console.log('Horarios ocupados encontrados:', occupiedTimes);
    return occupiedTimes;
  }

  // Normalizar el formato de tiempo para comparaciones consistentes
  private normalizeTimeFormat(time: string): string {
    if (!time) return time;
    
    // Dividir por el espacio para separar tiempo y AM/PM
    const parts = time.split(' ');
    if (parts.length !== 2) return time;
    
    const [timePart, period] = parts;
    const [hours, minutes] = timePart.split(':');
    
    // Normalizar las horas con padding de cero
    const normalizedHours = hours.padStart(2, '0');
    
    return `${normalizedHours}:${minutes} ${period}`;
  }

  // Convertir formato 24h a 12h para comparación
  private convertTo12Hour(time24h: string): string {
    // Ejemplo: "12:00" -> "12:00 PM", "15:00" -> "3:00 PM"
    const [hours, minutes] = time24h.split(':');
    let hour24 = parseInt(hours, 10);
    let hour12: number;
    let period: string;
    
    if (hour24 === 0) {
      hour12 = 12;
      period = 'AM';
    } else if (hour24 === 12) {
      hour12 = 12;
      period = 'PM';
    } else if (hour24 > 12) {
      hour12 = hour24 - 12;
      period = 'PM';
    } else {
      hour12 = hour24;
      period = 'AM';
    }
    
    // Formatear con padding para mantener consistencia
    const formattedHour = hour12.toString().padStart(2, '0');
    return `${formattedHour}:${minutes} ${period}`;
  }

  // Mapear appointment de la base de datos a formato Session
  private mapAppointmentToSession(appointment: any): Session {
    const appointmentDate = this.parseDateString(appointment.date);

    let time12h = appointment.time;
    if (appointment.time && !appointment.time.includes('AM') && !appointment.time.includes('PM')) {
      time12h = this.convertTo12Hour(appointment.time);
    }

    const appointmentCustomer = this.getCustomerForAppointment(appointment);

    let milestone = this.getMilestoneForUser(appointmentCustomer);

    let status: 'pending' | 'completed' | 'cancelled' = 'pending';
    const now = new Date();
    const appointmentDateTime = new Date(appointmentDate);
    
    if (appointment.time) {
      const time24h = this.convertTo24Hour(time12h);
      const [hours, minutes] = time24h.split(':');
      appointmentDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    }

    if (appointmentDateTime < now) {
      status = 'completed';
    }

    const files: AppointmentFile[] = Array.isArray(appointment.files)
      ? appointment.files.map((file: any) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          url: file.url,
          uploadDate: file.uploadDate ? new Date(file.uploadDate) : new Date()
        }))
      : [];

    const menteeName =
      appointmentCustomer?.name ||
      `${appointmentCustomer?.firstName || ''} ${appointmentCustomer?.lastName || ''}`.trim() ||
      appointment.customer?.name ||
      appointment.customer?.firstName ||
      'Cliente';

    const menteeAvatar = appointmentCustomer?.avatar || appointment.customer?.avatar || '/assets/icons/default-avatar.png';

    return {
      id: appointment._id || appointment.id || '',
      menteeName,
      menteeAvatar,
      date: appointmentDate,
      time: time12h,
      duration: appointment.duration || 60,
      milestone: milestone,
      status: status,
      sessionUrl: appointment.meetingUrl || '#',
      notes: appointment.notes || appointment.description || '',
      files: files
    };
  }

  // Convertir horario de 12h (AM/PM) a 24h para comparación correcta
  private convertTo24Hour(time12h: string): string {
    if (!time12h) return '00:00';
    
    // Si ya está en formato 24h, devolverlo tal como está
    if (!time12h.includes('AM') && !time12h.includes('PM')) {
      return time12h;
    }
    
    // Extraer hora, minutos y AM/PM
    const timeMatch = time12h.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!timeMatch) {
      console.warn('Formato de hora no reconocido:', time12h);
      return '00:00';
    }
    
    let hour = parseInt(timeMatch[1]);
    const minutes = timeMatch[2];
    const period = timeMatch[3].toUpperCase();
    
    // Convertir a formato 24h
    if (period === 'AM') {
      if (hour === 12) hour = 0; // 12:XX AM = 00:XX
    } else if (period === 'PM') {
      if (hour !== 12) hour += 12; // 1:XX PM = 13:XX, pero 12:XX PM = 12:XX
    }
    
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  }

  // Calcular la semana actual del usuario basado en createdAt
  private getCurrentWeekForUser(user: any): number {
    if (!user?.createdAt) {
      console.log('No createdAt found for user, returning week 1 as default');
      return 1;
    }

    const createdAt = new Date(user.createdAt);
    const now = new Date();
    
    console.log('User createdAt:', createdAt);
    console.log('Current date:', now);
    
    // Calcular la diferencia en milisegundos
    const diffInMs = now.getTime() - createdAt.getTime();
    
    // Convertir a días
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    console.log('Days since creation:', diffInDays);
    
    const week = Math.floor(diffInDays / 7) + 1;
    
    console.log('Calculated week for user:', week);
    
    return week;
  }

  // Determinar el hito basado en la semana actual del usuario
  private getMilestoneForUser(user: any): string {
    if (!user) {
      return 'Sesión de coaching';
    }

    const currentWeek = this.getCurrentWeekForUser(user);
    console.log('Determining milestone for week:', currentWeek);

    // Mapeo de semanas a hitos (basado en users-assigned-profile.component.ts)
    if (currentWeek >= 1 && currentWeek <= 2) {
      return 'Análisis profundo';
    } else if (currentWeek === 3) {
      return 'Mapa de identidad';
    } else if (currentWeek === 4) {
      return 'Phoenix Session';
    } else if (currentWeek === 5) {
      return 'Visionboard';
    } else if (currentWeek === 6) {
      return 'Phoenix Session';
    } else if (currentWeek >= 7 && currentWeek <= 9) {
      return 'PRD';
    } else if (currentWeek >= 10 && currentWeek <= 11) {
      return 'Plan de vida';
    } else if (currentWeek >= 12) {
      return 'Graduación';
    } else {
      return 'Iniciando programa';
    }
  }

  openNotesModal(session: Session): void {
    this.selectedAppointment = session;
    this.appointmentNotes = session.notes || '';
    this.appointmentFiles = session.files
      ? session.files.map(file => ({
          ...file,
          uploadDate: file.uploadDate ? new Date(file.uploadDate) : new Date()
        }))
      : [];
    this.showPreview = false;
    this.isSavingNotes = false;
    this.showNotesModal = true;

    setTimeout(() => {
      this.initializeQuillEditor();
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
    }, 300);
  }

  closeNotesModal(): void {
    this.showNotesModal = false;
    this.isSavingNotes = false;
    this.showPreview = false;
    this.appointmentNotes = '';
    this.appointmentFiles = [];
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.quillInstance = null;
    this.selectedAppointment = null;
  }

  togglePreview(): void {
    this.showPreview = !this.showPreview;
  }

  clearQuillEditor(): void {
    if (this.quillInstance) {
      this.quillInstance.setText('');
    }
    this.appointmentNotes = '';
  }

  triggerFileUpload(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    Array.from(input.files).forEach(file => this.uploadFile(file));
    input.value = '';
  }

  private uploadFile(file: File): void {
    const fileData: AppointmentFile = {
      name: file.name,
      size: file.size,
      type: file.type,
      uploadDate: new Date(),
      url: URL.createObjectURL(file)
    };

    this.appointmentFiles = [...this.appointmentFiles, fileData];
  }

  getFileIconClass(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return 'fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fa-file-word';
      case 'ppt':
      case 'pptx':
        return 'fa-file-powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'fa-file-image';
      case 'txt':
        return 'fa-file-alt';
      default:
        return 'fa-file';
    }
  }

  viewFile(file: AppointmentFile): void {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  }

  deleteFile(index: number): void {
    const [removed] = this.appointmentFiles.splice(index, 1);
    if (removed?.url && removed.url.startsWith('blob:')) {
      URL.revokeObjectURL(removed.url);
    }
    this.appointmentFiles = [...this.appointmentFiles];
  }

  saveAppointmentNotes(): void {
    if (!this.selectedAppointment || !this.selectedAppointment.id) {
      return;
    }

    this.isSavingNotes = true;

    const notesHtml = this.getQuillContent();
    this.appointmentNotes = notesHtml;

    const filesPayload = this.appointmentFiles.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type,
      url: file.url,
      uploadDate: file.uploadDate
    }));

    this.appointmentsService.updateNotesAndFiles(
      this.selectedAppointment.id,
      notesHtml,
      filesPayload
    ).subscribe({
      next: () => {
        this.isSavingNotes = false;
        if (this.selectedAppointment) {
          this.selectedAppointment.notes = notesHtml;
          this.selectedAppointment.files = this.appointmentFiles.map(file => ({ ...file }));
        }
        this.updateSessionCollections(this.selectedAppointment?.id || '', notesHtml, this.appointmentFiles);
        this.closeNotesModal();
        this.showAlert('Notas actualizadas', 'Las notas de la sesión se guardaron correctamente.', 'success');
      },
      error: (error) => {
        console.error('Error al guardar notas:', error);
        this.isSavingNotes = false;
        this.showAlert('Error', 'No se pudieron guardar las notas. Inténtalo de nuevo.', 'error');
      }
    });
  }

  private updateSessionCollections(sessionId: string, notes: string, files: AppointmentFile[]): void {
    const updateCollection = (collection: Session[]) => {
      const index = collection.findIndex(session => session.id === sessionId);
      if (index !== -1) {
        collection[index] = {
          ...collection[index],
          notes,
          files: files.map(file => ({ ...file }))
        };
      }
    };

    updateCollection(this.sessions);
    updateCollection(this.filteredSessions);
    updateCollection(this.paginatedSessions);
    this.paginatedSessions = [...this.paginatedSessions];
  }

  private initializeQuillEditor(): void {
    if (!this.quillEditor) {
      return;
    }

    // Destroy existing instance
    if (this.quillInstance) {
      this.quillInstance = null;
    }

    const toolbarOptions = [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      ['blockquote', 'code-block'],
      ['clean']
    ];

    const options = {
      theme: 'snow',
      placeholder: 'Escriba sus notas aquí...',
      modules: {
        toolbar: toolbarOptions
      },
      formats: [
        'header', 'bold', 'italic', 'underline', 'strike',
        'list', 'bullet', 'align', 'link', 'image',
        'blockquote', 'code-block'
      ]
    };

    setTimeout(() => {
      if (!this.quillEditor) {
        return;
      }

      try {
        this.quillInstance = new Quill(this.quillEditor.nativeElement, options);

        this.quillInstance.root.setAttribute('dir', 'ltr');
        this.quillInstance.root.style.direction = 'ltr';
        this.quillInstance.root.style.textAlign = 'left';

        this.quillInstance.on('text-change', () => {
          this.appointmentNotes = this.quillInstance?.root.innerHTML || '';
        });

        if (this.appointmentNotes) {
          this.quillInstance.root.innerHTML = this.appointmentNotes;
        }
      } catch (error) {
        console.error('Error al inicializar Quill:', error);
      }
    }, 200);
  }

  private getQuillContent(): string {
    return this.quillInstance?.root.innerHTML || this.appointmentNotes || '';
  }

  private populateAssignedCustomers(customers: any[]): void {
    this.assignedCustomersMap.clear();

    if (!Array.isArray(customers)) {
      return;
    }

    customers.forEach(customer => {
      const id = customer?._id || customer?.id;
      if (id) {
        this.assignedCustomersMap.set(id.toString(), customer);
      }
    });
  }

  private fetchCoachAppointments(): void {
    if (!this.user || !this.user._id) {
      this.sessions = [];
      this.applyFilters();
      this.loaderService.hide();
      return;
    }

    this.appointmentsService.findByCoach(this.user._id).subscribe({
      next: (response) => {
        console.log('📅 Citas del coach cargadas:', response);
        
        if (response && response.data && Array.isArray(response.data)) {
          this.sessions = response.data.map(appointment => this.mapAppointmentToSession(appointment));
          console.log('📅 Sesiones mapeadas:', this.sessions);
        } else {
          this.sessions = [];
        }
        
        this.applyFilters();
        this.loaderService.hide();
      },
      error: (error) => {
        console.error('Error al cargar citas del coach:', error);
        this.sessions = [];
        this.applyFilters();
        this.loaderService.hide();
      }
    });
  }

  private getCustomerForAppointment(appointment: any): any {
    if (!appointment) {
      return null;
    }

    const customerId =
      appointment.customer?._id ||
      appointment.customer?.id ||
      appointment.customerId ||
      appointment.customer;

    if (customerId) {
      const customerFromMap = this.assignedCustomersMap.get(customerId.toString());
      if (customerFromMap) {
        return customerFromMap;
      }
    }

    return appointment.customer || null;
  }

  private parseDateString(dateStr: string): Date {
    if (!dateStr) {
      return new Date();
    }

    const trimmed = dateStr.trim();

    if (trimmed.includes('T')) {
      const parsed = new Date(trimmed);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    const matches = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (matches) {
      const year = parseInt(matches[1], 10);
      const month = parseInt(matches[2], 10);
      const day = parseInt(matches[3], 10);
      return new Date(year, month - 1, day);
    }

    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    return new Date();
  }

  private getSessionTimestamp(session: Session): number {
    if (!session || !session.date) {
      return 0;
    }

    const date = new Date(session.date);

    if (!session.time) {
      return date.getTime();
    }

    const time24h = this.convertTo24Hour(session.time);
    if (!time24h) {
      return date.getTime();
    }

    const [hoursPart, minutesPart = '0'] = time24h.split(':');
    const hours = parseInt(hoursPart, 10);
    const minutes = parseInt(minutesPart, 10);

    if (!isNaN(hours)) {
      date.setHours(hours, isNaN(minutes) ? 0 : minutes, 0, 0);
    }

    return date.getTime();
  }
}
